import type { ReactNode } from "react";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";

type DataViewProps = {
  isLoading: boolean;
  isError?: boolean;
  error?: Error | null;
  isEmpty?: boolean;
  skeleton: ReactNode;
  empty?: ReactNode;
  errorState?: ReactNode;
  onRetry?: () => void;
  children: ReactNode;
};

/**
 * Standard wrapper for data-driven screens. Routes to one of:
 *   loading (delayed 150ms to avoid flash) → error → empty → children.
 *
 * Pair with a hook that returns { data, isLoading, isError, error, refetch }.
 */
export function DataView({
  isLoading,
  isError = false,
  error = null,
  isEmpty = false,
  skeleton,
  empty,
  errorState,
  onRetry,
  children,
}: DataViewProps) {
  const showSkeleton = useDelayedLoading(isLoading);

  if (isLoading) return <>{showSkeleton ? skeleton : null}</>;

  if (isError) {
    if (errorState) return <>{errorState}</>;
    return (
      <div className="flex flex-col items-center justify-center gap-3 p-6 text-center text-sm text-muted-foreground">
        <p>{error?.message ?? "Something went wrong."}</p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-md border border-input bg-background px-3 py-1.5 text-foreground hover:bg-accent"
          >
            Try again
          </button>
        ) : null}
      </div>
    );
  }

  if (isEmpty) return <>{empty ?? null}</>;

  return <>{children}</>;
}

export default DataView;
